// Phase 1 dual-write helpers — see CLAUDE.md "Vendor data integrity".
//
// JSON blob (app_storage.csid_vendors) stays authoritative; the vendors
// SQL table is best-effort mirrored so Phase 2 can flip consumers to
// id-based reads. Failures are logged; the JSON write is the source of
// truth. Extracted to this module 2026-05-29 so every write surface
// (VendorDirectory.save / del / pushVendorToQb / generateResaleCertState,
// AppContext.addVendorInline / restoreData, QuickBooksTab pushVendor) can
// share one canonical implementation. Phase 3 will invert the contract
// here — make the SQL write blocking and the blob write the mirror.

import { supabase } from './supabase';
import { vendorToRow } from '../utils';

// Single-row upsert. Used by every per-vendor write site.
export function dualWriteVendorRow(vendor) {
  var row = vendorToRow(vendor);
  row.updated_at = new Date().toISOString();
  return supabase.from('vendors').upsert(row, { onConflict: 'legacy_id' }).then(function(r) {
    if (r && r.error) console.error('vendors-table dual-write failed', r.error);
  }).catch(function(e) { console.error('vendors-table dual-write threw', e); });
}

// Bulk upsert for restore-from-backup. Single round-trip instead of N
// individual upserts. Restored rows that aren't currently in the SQL
// table get created; rows already present get updated. SQL rows whose
// legacy_id is NOT in the backup are intentionally NOT deleted —
// restore is additive on the SQL side to avoid destroying data that
// was added after the backup was taken. Phase 3 (when SQL becomes
// canonical) will revisit this semantic.
export function bulkDualWriteVendorRows(vendors) {
  if (!Array.isArray(vendors) || vendors.length === 0) return Promise.resolve();
  var now = new Date().toISOString();
  var rows = vendors.map(function(v) {
    var row = vendorToRow(v);
    row.updated_at = now;
    return row;
  });
  return supabase.from('vendors').upsert(rows, { onConflict: 'legacy_id' }).then(function(r) {
    if (r && r.error) console.error('vendors-table bulk dual-write failed', r.error);
  }).catch(function(e) { console.error('vendors-table bulk dual-write threw', e); });
}

// Delete sibling. Keys on legacy_id (the JSON-blob integer id). A
// null/non-numeric id short-circuits — picker-added vendors that
// pre-date Phase 1's Date.now() assign carry no legacy_id in the SQL
// row, so the SQL deletion would silently affect zero rows anyway;
// we keep the JSON-blob delete on its own path.
export function dualDeleteVendorRow(legacyId) {
  if (typeof legacyId !== 'number') return Promise.resolve();
  return supabase.from('vendors').delete().eq('legacy_id', legacyId).then(function(r) {
    if (r && r.error) console.error('vendors-table delete dual-write failed', r.error);
  }).catch(function(e) { console.error('vendors-table delete threw', e); });
}
